import { Button } from '@/shared/components/ui/Button';
import { useEscapeClose } from '@/shared/hooks/useEscapeClose';
import { ApiError } from '@/shared/lib/api-client';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2, X } from 'lucide-react';
import { useState } from 'react';
import { type ContactRow, createContact, updateContact } from './api';
import { useRefetchOnOpen } from '@/shared/hooks/useRefetchOnOpen';
import { useAccessGroups, useContactTags } from './hooks';

interface Props {
  contact: ContactRow | null;
  onClose: () => void;
}

export function ContactFormModal({ contact, onClose }: Props) {
  useEscapeClose(true, onClose);
  const qc = useQueryClient();
  const editing = contact !== null;
  const tagsQ = useContactTags();
  useRefetchOnOpen(true, tagsQ.refetch);
  const groupsQ = useAccessGroups();
  const groupsEnabled = (groupsQ.data ?? []).length > 0;

  const [name, setName] = useState(contact?.display_name ?? '');
  const [phone, setPhone] = useState(contact?.primary_identifier?.value ?? '');
  const [email, setEmail] = useState(contact?.email ?? '');
  const [notes, setNotes] = useState(contact?.notes ?? '');
  const [tagIds, setTagIds] = useState<number[]>(contact?.tags.map((t) => t.id) ?? []);
  const [accessGroupIds, setAccessGroupIds] = useState<number[]>(
    contact?.access_groups.map((g) => g.id) ?? [],
  );
  const [error, setError] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: async () => {
      const payload = {
        display_name: name.trim(),
        phone: phone.trim().startsWith('+') ? phone.trim() : `+${phone.trim().replace(/\D/g, '')}`,
        ...(email.trim() && { email: email.trim() }),
        ...(notes.trim() && { notes: notes.trim() }),
        tag_ids: tagIds,
        access_group_ids: accessGroupIds,
      };
      if (editing && contact) {
        await updateContact(contact.id, payload);
      } else {
        await createContact(payload);
      }
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['contacts'] });
      onClose();
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        setError(err.problem.detail ?? err.problem.title);
      } else {
        setError('Falha ao salvar.');
      }
    },
  });

  const submit = (): void => {
    setError(null);
    if (!name.trim()) return setError('Informe o nome');
    if (!phone.trim()) return setError('Informe o telefone');
    mut.mutate();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="w-[480px] max-w-full rounded-lg bg-n-0 p-6 shadow-lg">
        <div className="flex items-center justify-between">
          <h2 className="font-bold text-[16px] text-n-900">
            {editing ? 'Editar contato' : 'Adicionar contato'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-md text-n-500 hover:bg-n-100"
            aria-label="Fechar"
          >
            <X size={16} />
          </button>
        </div>

        <div className="mt-5 flex flex-col gap-3.5">
          <Field label="Nome">
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="input-base"
              placeholder="Nome completo"
              autoFocus
            />
          </Field>
          <Field label="WhatsApp (E.164, ex: +5511999990000)">
            <input
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="input-base"
              placeholder="+5511999990000"
              disabled={editing}
              title={editing ? 'Telefone não pode ser editado após a criação' : undefined}
            />
          </Field>
          <Field label="Email (opcional)">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="input-base"
              placeholder="cliente@empresa.com"
            />
          </Field>
          <Field label="Etiquetas">
            <div className="flex flex-wrap gap-1.5">
              {(tagsQ.data ?? []).map((t) => {
                const active = tagIds.includes(t.id);
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => {
                      setTagIds((prev) =>
                        active ? prev.filter((id) => id !== t.id) : [...prev, t.id],
                      );
                    }}
                    className={`rounded-pill border px-2.5 py-1 font-semibold text-[10px] transition ${
                      active
                        ? 'border-transparent text-white'
                        : 'border-n-200 bg-n-0 text-n-700 hover:border-brand-300'
                    }`}
                    style={active ? { background: t.color } : undefined}
                  >
                    {t.name}
                  </button>
                );
              })}
            </div>
          </Field>
          {groupsEnabled && (
            <Field label="Grupos de acesso">
              <div className="flex flex-wrap gap-1.5">
                {(groupsQ.data ?? []).map((g) => {
                  const active = accessGroupIds.includes(g.id);
                  return (
                    <button
                      key={g.id}
                      type="button"
                      onClick={() => {
                        setAccessGroupIds((prev) =>
                          active ? prev.filter((id) => id !== g.id) : [...prev, g.id],
                        );
                      }}
                      className={`rounded-pill border px-2.5 py-1 font-semibold text-[10px] transition ${
                        active
                          ? 'border-transparent bg-brand-500 text-white'
                          : 'border-n-200 bg-n-0 text-n-700 hover:border-brand-300'
                      }`}
                    >
                      {g.name}
                    </button>
                  );
                })}
              </div>
            </Field>
          )}
          <Field label="Observações (opcional)">
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="input-base min-h-[72px] resize-y"
              placeholder="Notas internas sobre o contato"
            />
          </Field>
        </div>

        {error && (
          <div className="mt-3 rounded-md bg-danger-50 px-3 py-2 text-[12px] text-danger-700">
            {error}
          </div>
        )}

        <div className="mt-5 flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={mut.isPending}>
            Cancelar
          </Button>
          <Button onClick={submit} disabled={mut.isPending}>
            {mut.isPending && <Loader2 size={12} className="animate-spin" />}
            {editing ? 'Salvar' : 'Criar contato'}
          </Button>
        </div>

        <style>{`.input-base{display:block;width:100%;border:1px solid var(--n-200);background:var(--bg-surface);border-radius:6px;padding:8px 12px;font-size:13px;color:var(--n-900);outline:none}.input-base:focus{border-color:var(--brand-500)}`}</style>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="font-semibold text-[12px] text-n-700">{label}</span>
      {children}
    </label>
  );
}
